import { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import useAuth from './hooks/useAuth';
import { BACKEND_URL } from './config';
import useAuthCheck from './hooks/useAuthCheck';
import useTheme from './hooks/useTheme';
import ScrollToTop from './components/ScrollToTop';
import Header from './components/Header';
import Footer from './components/Footer';
import ChatWidget from './components/ChatWidget';
import Home from './pages/Home';
import Projects from './pages/Projects';
import Project from './pages/Project';
import NewProject from './pages/NewProject';
import EditProject from './pages/EditProject';
import Formations from './pages/Formations';
import NewFormation from './pages/NewFormation';
import EditFormation from './pages/EditFormation';
import Login from './pages/Login';
import Admin from './pages/Admin';
import ManageSkillsRoles from './pages/ManageSkillsRoles';
import PdfViewer from './pages/PdfViewer';
import NotFound from './pages/NotFound';
import './i18n';

function AnimatedRoutes({ isAuthenticated }: { isAuthenticated: boolean }) {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home authenticated={isAuthenticated} />} />
        <Route path="/formations" element={<Formations authenticated={isAuthenticated} />} />
        <Route path="/formations/new" element={isAuthenticated ? <NewFormation /> : <Navigate to="/not-found" />} />
        <Route path="/formations/edit/:strid" element={isAuthenticated ? <EditFormation /> : <Navigate to="/not-found" />} />
        <Route path="/projects" element={<Projects authenticated={isAuthenticated} />} />
        <Route path="/projects/:strid" element={<Project authenticated={isAuthenticated} />} />
        <Route path="/projects/new" element={isAuthenticated ? <NewProject /> : <Navigate to="/not-found" />} />
        <Route path="/projects/edit/:strid" element={isAuthenticated ? <EditProject /> : <Navigate to="/not-found" />} />
        <Route path="/login" element={<Login authenticated={isAuthenticated} />} />
        <Route path="/admin" element={isAuthenticated ? <Admin /> : <Navigate to="/not-found" />} />
        <Route path="/admin/manage" element={isAuthenticated ? <ManageSkillsRoles /> : <Navigate to="/not-found" />} />
        <Route path="/cv" element={<PdfViewer authenticated={isAuthenticated} />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [linkedIn, setLinkedIn] = useState('');
  const [github, setGithub] = useState('');
  const [mail, setMail] = useState('');
  const [phone, setPhone] = useState('');

  useAuthCheck(setIsAuthenticated);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/profile`)
      .then(r => r.json())
      .then(data => {
        if (data) {
          setLinkedIn(data.linkedIn || '');
          setGithub(data.github || '');
          setMail(data.mail || '');
          setPhone(data.phone || '');
        }
      })
      .catch(console.error);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('admin');
    setIsAuthenticated(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-base">
      <ScrollToTop />
      <Header
        isAuthenticated={isAuthenticated}
        linkedIn={linkedIn}
        github={github}
        mail={mail}
        phone={phone}
        onLogout={handleLogout}
        theme={theme}
        onToggleTheme={toggleTheme}
      />
      <main className="flex-1">
        <AnimatedRoutes isAuthenticated={isAuthenticated} />
      </main>
      <Footer linkedIn={linkedIn} github={github} mail={mail} />
      <ChatWidget />
    </div>
  );
}

export default function WrappedApp() {
  return (
    <Router>
      <App />
    </Router>
  );
}
